import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown, Check } from 'lucide-react';

interface Option {
  value: string;
  label: string;
  sublabel?: string;
  logo?: string;
  icon?: React.ReactNode;
}

interface SearchableSelectProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  hideSelectedSublabel?: boolean;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option...',
  label,
  disabled = false,
  hideSelectedSublabel = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);

  const filteredOptions = options.filter((opt) => {
    const query = searchQuery.toLowerCase();
    return (
      opt.label.toLowerCase().includes(query) ||
      (opt.sublabel && opt.sublabel.toLowerCase().includes(query)) ||
      opt.value.toLowerCase().includes(query)
    );
  });

  return (
    <div className="flex flex-col gap-1.5 w-full relative" ref={containerRef}>
      {label && (
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
          {label}
        </label>
      )}
      
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setIsOpen(!isOpen);
          setSearchQuery('');
        }}
        className={`
          w-full px-4 py-2.5 bg-white dark:bg-slate-900 border rounded-xl text-sm
          flex items-center justify-between text-left transition-all duration-200 outline-none
          border-slate-200 dark:border-slate-700 focus:border-[--primary] focus:ring-4 focus:ring-[--primary]/10
          disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 dark:text-white
        `}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          {selectedOption?.icon ? (
            <span className="shrink-0">{selectedOption.icon}</span>
          ) : selectedOption?.logo ? (
            <img src={selectedOption.logo} alt="" className="w-5 h-5 rounded-md object-contain bg-slate-50 dark:bg-slate-800 p-0.5 shrink-0" />
          ) : null}
          <div className="min-w-0">
            <div className="font-medium truncate">{selectedOption ? selectedOption.label : placeholder}</div>
            {selectedOption?.sublabel && !hideSelectedSublabel && (
              <div className="text-xs text-slate-400 dark:text-slate-500 truncate mt-0.5">{selectedOption.sublabel}</div>
            )}
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform shrink-0 ml-2 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
          {/* Search Input */}
          <div className="relative p-2.5 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/20">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-1.5 text-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg outline-none focus:border-[--primary] text-slate-900 dark:text-white"
              autoFocus
            />
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto py-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => {
                const isSelected = opt.value === value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      setIsOpen(false);
                    }}
                    className={`
                      w-full px-4 py-2 flex items-center justify-between text-left text-sm transition-colors
                      ${isSelected 
                        ? 'bg-orange-50/50 dark:bg-orange-500/10 text-[--primary] font-medium' 
                        : 'hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                      }
                    `}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      {opt.icon ? (
                        <span className="shrink-0">{opt.icon}</span>
                      ) : opt.logo ? (
                        <img src={opt.logo} alt="" className="w-5 h-5 rounded-md object-contain bg-slate-50 dark:bg-slate-800 p-0.5 shrink-0" />
                      ) : null}
                      <div className="min-w-0">
                        <div className="truncate">{opt.label}</div>
                        {opt.sublabel && (
                          <div className="text-xs text-slate-400 dark:text-slate-500 truncate mt-0.5">{opt.sublabel}</div>
                        )}
                      </div>
                    </div>
                    {isSelected && <Check className="w-4 h-4 text-[--primary] shrink-0 ml-2" />}
                  </button>
                );
              })
            ) : (
              <div className="px-4 py-3 text-sm text-slate-400 dark:text-slate-500 text-center">No options found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
